import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ToastContext from '../contexts/ToastContext';
import { listEvents, deleteEvent, joinEvent as joinSupabaseEvent, leaveEvent as leaveSupabaseEvent, reportEvent as reportSupabaseEvent } from '../utils/eventsApi';
import { FiCircle } from 'react-icons/fi';
import { FaFootballBall, FaBasketballBall, FaTableTennis, FaSwimmer, FaRunning, FaBicycle, FaGamepad } from 'react-icons/fa';
import { MdDateRange, MdLocationOn, MdPerson, MdTrendingUp, MdSearch, MdEdit, MdDelete, MdCheckCircle, MdCancel } from 'react-icons/md';
import { isDemoAdmin, DEMO_ADMIN_MESSAGE } from '../utils/adminDemoMode';

export default function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const toast = useContext(ToastContext);
  const [filters, setFilters] = useState({ sportType: '', date: '', location: '' });
  const [showMyEvents, setShowMyEvents] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [clickedSport, setClickedSport] = useState(null);
  const [tooltipSport, setTooltipSport] = useState(null);
  const [hoveredSport, setHoveredSport] = useState(null);

  const sportIconMap = {
    'Football': FaFootballBall,
    'Basketball': FaBasketballBall,
    'Tennis': FaTableTennis,
    'Natation': FaSwimmer,
    'Course': FaRunning,
    'Cyclisme': FaBicycle,
    'Autre': FaGamepad
  };

  const getSportIcon = (sportName) => {
    const IconComponent = sportIconMap[sportName] || FaGamepad;
    return <IconComponent size={48} color="var(--accent)" />;
  };

  const sports = [
    { name: 'Football', image: '/football.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Le roi des sports' },
    { name: 'Basketball', image: '/basketball.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Sport d\'équipe dynamique' },
    { name: 'Tennis', image: '/tennis.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Sport de raquette classique' },
    { name: 'Natation', image: '/natation.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Sport aquatique' },
    { name: 'Course', image: '/course.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Sport d\'endurance' },
    { name: 'Cyclisme', image: '/cyclisme.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Sport sur deux roues' },
    { name: 'Autre', image: '/autre.jpg', gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(0, 184, 148, 0.12))', description: 'Explorez d\'autres sports' }
  ];

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const sportFilter = selectedSport || filters.sportType;
      const data = await listEvents({
        sportType: sportFilter,
        date: filters.date,
        location: filters.location,
        organizer: showMyEvents ? currentUser?._id : undefined
      });
      setEvents(data);
    } catch (err) {
      setError('Erreur lors du chargement des événements');
    }
    setLoading(false);
  }, [filters, showMyEvents, currentUser, selectedSport]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  async function removeEvent(id) {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) return;
    console.log('Attempting to delete event', id);
    console.log('Current user:', currentUser);
    console.log('User is admin?', currentUser?.isAdmin);
    try {
      await deleteEvent(id);
      console.log('Delete request succeeded', result);
      setEvents(events.filter(e => e._id !== id));
      if (toast && toast.success) {
        console.log('Showing success toast');
        toast.success('Événement supprimé');
      } else {
        console.log('Toast not available', toast);
      }
    } catch (err) {
      console.log('Delete request failed', err);
      if (toast && toast.error) {
        console.log('Showing error toast');
        toast.error('Erreur lors de la suppression');
      }
    }
  }

  async function joinEvent(id) {
    try {
      await joinSupabaseEvent(id, currentUser.id || currentUser._id);
      fetchEvents(); // Refresh list
      if (toast && toast.success) toast.success('Inscrit à l\'événement');
    } catch (err) {
      if (toast && toast.error) toast.error('Erreur lors de l\'inscription');
    }
  }

  async function leaveEvent(id) {
    try {
      await leaveSupabaseEvent(id, currentUser.id || currentUser._id);
      fetchEvents(); // Refresh list
      if (toast && toast.success) toast.success('Désinscrit de l\'événement');
    } catch (err) {
      if (toast && toast.error) toast.error('Erreur lors de la désinscription');
    }
  }

  async function reportEvent(id) {
    const reason = prompt('Raison du signalement :');
    if (!reason) return;
    try {
      await reportSupabaseEvent(id, currentUser.id || currentUser._id, reason);
      if (toast && toast.success) toast.success('Événement signalé');
    } catch (err) {
      if (toast && toast.error) toast.error('Erreur lors du signalement');
    }
  }

  function handleEdit(event) {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      return;
    }
    navigate(`/create-event?eventId=${event._id}`);
  }

  function getEventStatus(event) {
    const now = new Date();
    const eventDateTime = new Date(`${event.date.split('T')[0]}T${event.startTime}`);
    const eventEndTime = event.endTime ? new Date(`${event.date.split('T')[0]}T${event.endTime}`) : eventDateTime;

    if (now > eventEndTime) {
      return { icon: <FiCircle size={16} />, text: 'Terminé', color: '#000' };
    } else if (now >= eventDateTime && now <= eventEndTime) {
      return { icon: <FiCircle size={16} />, text: 'En cours', color: '#fbbf24' };
    } else {
      if (event.maxParticipants > 0 && event.participants.length >= event.maxParticipants) {
        return { icon: <FiCircle size={16} />, text: 'Complet', color: '#ef4444' };
      } else {
        return { icon: <FiCircle size={16} />, text: 'Ouvert', color: '#10b981' };
      }
    }
  }

  return (
    <div className="container">
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(-10px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .event-card {
            animation: fadeInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
            transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            position: relative;
            overflow: hidden;
          }

          .event-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255, 0.3), transparent);
            transition: left 0.6s ease;
            pointer-events: none;
          }

          .event-card:hover::before {
            left: 100%;
          }

          .event-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(56, 189, 248, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.8);
            border-color: rgba(56, 189, 248, 0.18);
          }

          .event-status-badge {
            animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s backwards;
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .event-status-badge:hover {
            transform: scale(1.05) translateY(-2px);
            box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
          }

          .event-metadata {
            animation: slideInRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            padding: 0.75rem 0 !important;
            border-bottom: 1px solid rgba(56, 189, 248, 0.05) !important;
          }
          
          .event-metadata:nth-child(1) { animation-delay: 0.4s; }
          .event-metadata:nth-child(2) { animation-delay: 0.5s; }
          .event-metadata:nth-child(3) { animation-delay: 0.6s; }
          .event-metadata:nth-child(4) { animation-delay: 0.7s; }

          .event-metadata:last-child {
            border-bottom: none !important;
          }

          .event-metadata:hover {
            background: rgba(56, 189, 248, 0.03);
            padding-left: 0.5rem;
          }

          .participant-avatar {
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            border: 3px solid rgba(255, 255, 255, 0.95);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            position: relative;
            z-index: 1;
          }

          .participant-avatar:hover {
            transform: scale(1.2) translateY(-6px);
            z-index: 30;
            box-shadow: 0 12px 24px rgba(56, 189, 248, 0.35);
            border-color: rgba(56, 189, 248, 0.5);
          }

          .participant-avatar:nth-child(n+2) {
            margin-left: -16px;
          }

          .event-btn {
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            border-radius: 14px;
            font-weight: 600;
            padding: 0.75rem 1.5rem;
            font-size: 0.9rem;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            cursor: pointer;
            border: 2px solid transparent;
            position: relative;
            overflow: hidden;
          }

          .event-btn::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.1);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
            pointer-events: none;
          }

          .event-btn:hover::before {
            width: 300px;
            height: 300px;
          }

          .event-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(56, 189, 248, 0.2);
          }

          .event-btn:active {
            transform: translateY(0);
          }

          .event-btn-primary {
            background: linear-gradient(135deg, var(--accent), var(--accent-2));
            color: white;
            box-shadow: 0 4px 12px rgba(56, 189, 248, 0.2);
          }

          .event-btn-primary:hover {
            background: linear-gradient(135deg, var(--accent-2), var(--accent));
            box-shadow: 0 8px 20px rgba(56, 189, 248, 0.3);
          }

          .event-btn-secondary {
            background: rgba(56, 189, 248, 0.1);
            color: var(--accent);
            border-color: rgba(56, 189, 248, 0.3);
          }

          .event-btn-secondary:hover {
            background: rgba(56, 189, 248, 0.2);
            border-color: var(--accent);
          }

          .event-btn-danger {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
            border-color: rgba(239, 68, 68, 0.3);
          }

          .event-btn-danger:hover {
            background: rgba(239, 68, 68, 0.2);
            border-color: #ef4444;
          }

          .filter-input {
            background: rgba(56, 189, 248, 0.05);
            border: 2px solid rgba(56, 189, 248, 0.2);
            border-radius: 12px;
            padding: 0.75rem 1rem;
            transition: all 0.3s ease;
            font-size: 0.95rem;
          }

          .filter-input:focus {
            outline: none;
            background: rgba(56, 189, 248, 0.1);
            border-color: var(--accent);
            box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.1);
          }

          .sports-carousel::-webkit-scrollbar {
            height: 6px;
          }

          .sports-carousel::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 10px;
          }

          .sports-carousel::-webkit-scrollbar-thumb {
            background: rgba(56, 189, 248, 0.3);
            border-radius: 10px;
          }

          .sports-carousel::-webkit-scrollbar-thumb:hover {
            background: rgba(56, 189, 248, 0.5);
          }

          @keyframes iconBounce {
            0%, 100% {
              transform: translateY(0) scale(1);
            }
            50% {
              transform: translateY(-8px) scale(1.1);
            }
          }

          @keyframes cardEnter {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (max-width: 768px) {
            .sports-carousel {
              gap: 1rem !important;
            }

            .sport-card-carousel {
              min-width: 140px !important;
            }

            .events-grid-responsive {
              width: 100% !important;
            }
          }
        `}
      </style>
      <h2 style={{
        fontSize: 'clamp(24px, 2vw, 32px)',
        fontWeight: 700,
        marginBottom: '2rem',
        background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textShadow: '0 4px 20px rgba(56, 189, 248, 0.1)',
        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
      }}>{selectedSport ? `Événements de ${selectedSport}` : 'Événements sportifs'}</h2>

      {!selectedSport ? (
        <div>
          <h3 style={{ marginBottom: '1.5rem', fontSize: '1.2rem', fontWeight: 700, color: 'var(--text)' }}>Sélectionnez un sport</h3>
          {/* Carousel Container */}
          <div 
            ref={carouselRef}
            className="sports-carousel"
            onMouseDown={(e) => {
              setIsDragging(true);
              setDragStart(e.clientX);
            }}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onMouseMove={(e) => {
              if (!isDragging) return;
              const diff = e.clientX - dragStart;
              if (carouselRef.current) {
                carouselRef.current.scrollLeft -= diff;
                setDragStart(e.clientX);
              }
            }}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1.25rem',
              paddingBottom: '0.5rem',
              marginBottom: '2rem',
              justifyContent: 'flex-start'
            }}
          >
            {sports.map((sport, index) => {
              const sportColors = {
                'Football': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Basketball': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Tennis': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Natation': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Course': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Cyclisme': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' },
                'Autre': { border: 'rgba(6, 182, 212, 0.25)', hover: 'rgba(6, 182, 212, 0.3)' }
              };
              const colors = sportColors[sport.name];
              return (
              <div 
                key={sport.name} 
                className="sport-card-carousel"
                style={{
                  width: '160px',
                  minWidth: '160px',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  padding: '1rem',
                  borderRadius: '20px',
                  background: clickedSport === sport.name ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.25), rgba(0, 184, 148, 0.2))' : sport.gradient,
                  border: `1px solid ${colors.border}`,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.05}s forwards`,
                  opacity: 0,
                  transform: 'translateY(20px)',
                  position: 'relative',
                  overflow: 'visible',
                  boxShadow: clickedSport === sport.name ? '0 0 20px rgba(6, 182, 212, 0.4), 0 10px 20px rgba(0, 0, 0, 0.15)' : 'none'
                }}
                onClick={(e) => {
                  setSelectedSport(sport.name);
                  setClickedSport(sport.name);
                  setTimeout(() => setClickedSport(null), 600);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = sport.gradient;
                  e.currentTarget.style.borderColor = colors.hover;
                  e.currentTarget.style.transform = 'scale(1.05) translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.15)';
                  setTooltipSport(sport.name);
                  setHoveredSport(sport.name);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = sport.gradient;
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  setTooltipSport(null);
                  setHoveredSport(null);
                }}
              >
                {clickedSport === sport.name && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '20px',
                      background: 'radial-gradient(circle, rgba(6, 182, 212, 0.8), transparent)',
                      animation: 'ripple 0.6s ease-out',
                      pointerEvents: 'none'
                    }}
                    className="ripple-effect"
                  />
                )}
                {tooltipSport === sport.name && sport.name === 'Autre' && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-35px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      zIndex: 10,
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(6, 182, 212, 0.3)',
                      animation: 'tooltipFadeIn 0.3s ease-out'
                    }}
                  >
                    Explorez d'autres sports
                  </div>
                )}
                <div
                  style={{
                    transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: hoveredSport === sport.name ? 'translateY(-8px) scale(1.1)' : 'translateY(0) scale(1)',
                  }}
                >
                  {getSportIcon(sport.name)}
                </div>
                <h4 
                  style={{ 
                    margin: 0, 
                    fontSize: '0.95rem', 
                    fontWeight: 600,
                    color: 'var(--text)',
                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.fontWeight = '700';
                    e.currentTarget.style.transform = 'scale(1.08)';
                    e.currentTarget.style.color = 'var(--accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.fontWeight = '600';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.color = 'var(--text)';
                  }}
                >
                  {sport.name}
                </h4>
                <div 
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.1))`,
                    borderRadius: '20px',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }}
                  className="sport-overlay"
                />
              </div>
            );
            })}
          </div>
        </div>
      ) : (
        <div className="events-container" style={{ marginBottom: '2rem' }}>
          <button className="btn btn-secondary" onClick={() => setSelectedSport(null)} style={{marginBottom:'1.5rem'}}>← Retour aux sports</button>
          <div className="filters" style={{display:'flex', gap:'1rem', marginBottom:'2rem', flexWrap:'wrap', alignItems:'flex-end'}}>
            <div className="field" style={{flex:'1', minWidth:'200px', display:'flex', flexDirection:'column', gap:'0.5rem'}}>
              <label className="muted" style={{fontSize:'0.85rem', fontWeight:600}}>📅 Date</label>
              <input className="filter-input" type="date" value={filters.date} onChange={e => setFilters({...filters, date: e.target.value})} />
            </div>
            <div className="field" style={{flex:'1', minWidth:'200px', display:'flex', flexDirection:'column', gap:'0.5rem'}}>
              <label className="muted" style={{fontSize:'0.85rem', fontWeight:600}}>📍 Lieu</label>
              <input className="filter-input" type="text" value={filters.location} onChange={e => setFilters({...filters, location: e.target.value})} placeholder="Rechercher par lieu..." />
            </div>
            {currentUser && (
              <div className="field">
                <button className="event-btn event-btn-secondary" onClick={() => setShowMyEvents(!showMyEvents)} style={{marginBottom:0}}>
                  {showMyEvents ? '✓ Mes événements' : 'Mes événements'}
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Chargement...</div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: 'var(--danger)', padding: '2rem' }}>{error}</div>
          ) : events.length === 0 ? (
            <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '2rem' }}>Aucun événement trouvé.</p>
          ) : (
        <div className="events-grid-responsive" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem',
          marginBottom: '2rem',
          paddingBottom: '1rem'
        }}>
          {events.map(event => {
            const isJoined = currentUser && event.participants.filter(p => p).some(p => p._id === currentUser._id);
            const isOrganizer = currentUser && event.organizer && currentUser._id === event.organizer._id;
            const status = getEventStatus(event);
            return (
              <div key={event._id} className="event-card" style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                border: '1.5px solid rgba(56, 189, 248, 0.12)',
                borderRadius: '24px',
                padding: '1.75rem',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                maxWidth: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                {/* Status Badge - Modern Pill */}
                <div className="event-status-badge" style={{
                  position: 'absolute',
                  top: '1.75rem',
                  right: '1.75rem',
                  background: status.color === '#10b981' ? 'rgba(16, 185, 129, 0.1)' : 
                              status.color === '#fbbf24' ? 'rgba(251, 191, 36, 0.1)' :
                              status.color === '#ef4444' ? 'rgba(239, 68, 68, 0.1)' :
                              'rgba(107, 114, 128, 0.08)',
                  color: status.color,
                  padding: '0.5rem 1.2rem',
                  borderRadius: '50px',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  border: `1px solid ${status.color}33`,
                  backdropFilter: 'blur(12px)',
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                  boxShadow: 'none'
                }}>
                  {status.text === 'Ouvert' && <MdCheckCircle size={16} />}
                  {status.text === 'Terminé' && <MdCancel size={16} />}
                  {status.text === 'En cours' && <MdCheckCircle size={16} />}
                  {status.text === 'Complet' && <MdCancel size={16} />}
                  {status.text}
                </div>

                {/* Sport Title - Enhanced Premium Typography */}
                <h3 style={{
                  fontSize: 'clamp(1.3rem, 2.8vw, 1.6rem)',
                  fontWeight: 900,
                  margin: '0.8rem 0 1.5rem 0',
                  background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.6px',
                  lineHeight: 1.2,
                  animation: 'slideInRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards'
                }}>
                  {event.sportType}
                </h3>

                {/* Metadata Section - Enhanced Spacing */}
                <div style={{display:'flex', flexDirection:'column', gap:'0', marginBottom:'1.25rem', flex:1}}>
                  {/* Date & Time */}
                  <div className="event-metadata" style={{display:'flex', alignItems:'flex-start', gap:'1.2rem'}}>
                    <MdDateRange size={18} style={{color:'var(--accent)', flexShrink:0, marginTop:'0.2rem'}} />
                    <div style={{flex:1}}>
                      <p style={{margin:0, fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px'}}>Date & Heure</p>
                      <p style={{margin:'0.3rem 0 0 0', fontSize:'0.95rem', fontWeight:600, color:'var(--text)', lineHeight:1.4}}>
                        {new Date(event.date).toLocaleDateString('fr-FR', {weekday:'long', year:'numeric', month:'long', day:'numeric'})} à {event.startTime}
                        {event.endTime && ` - ${event.endTime}`}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="event-metadata" style={{display:'flex', alignItems:'flex-start', gap:'1.2rem'}}>
                    <MdLocationOn size={18} style={{color:'var(--accent)', flexShrink:0, marginTop:'0.2rem'}} />
                    <div style={{flex:1}}>
                      <p style={{margin:0, fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px'}}>Lieu</p>
                      <p style={{margin:'0.3rem 0 0 0', fontSize:'0.95rem', fontWeight:600, color:'var(--text)', lineHeight:1.4}}>
                        {event.location}
                      </p>
                    </div>
                  </div>

                  {/* Organizer */}
                  <div className="event-metadata" style={{display:'flex', alignItems:'flex-start', gap:'1.2rem'}}>
                    <MdPerson size={18} style={{color:'var(--accent)', flexShrink:0, marginTop:'0.2rem'}} />
                    <div style={{flex:1}}>
                      <p style={{margin:0, fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px'}}>Organisateur</p>
                      <p style={{margin:'0.3rem 0 0 0', fontSize:'0.95rem', fontWeight:600, color:'var(--text)', cursor:'pointer', transition:'color 0.3s', lineHeight:1.4}}
                        onClick={() => event.organizer && navigate(`/user/${event.organizer._id}`)}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text)'}
                      >
                        {event.organizer ? event.organizer.displayName : 'Supprimé'}
                      </p>
                    </div>
                  </div>

                  {/* Level */}
                  <div className="event-metadata" style={{display:'flex', alignItems:'flex-start', gap:'1.2rem'}}>
                    <MdTrendingUp size={18} style={{color:'var(--accent)', flexShrink:0, marginTop:'0.2rem'}} />
                    <div style={{flex:1}}>
                      <p style={{margin:0, fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.3px'}}>Niveau</p>
                      <p style={{margin:'0.3rem 0 0 0', fontSize:'0.95rem', fontWeight:600, color:'var(--text)', lineHeight:1.4}}>
                        {event.level}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Participants Section - Enhanced */}
                {isOrganizer && event.participants.length > 0 ? (
                  <div style={{marginBottom:'1rem', paddingBottom:'1rem', borderTop:'1px solid rgba(56, 189, 248, 0.05)', borderBottom:'1px solid rgba(56, 189, 248, 0.05)', paddingTop:'1rem'}}>
                    <p style={{margin:'0 0 0.9rem 0', fontSize:'0.8rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px'}}>
                      👥 Participants ({event.participants.filter(p => p).length}{event.maxParticipants > 0 ? ` / ${event.maxParticipants}` : ''})
                    </p>
                    <div style={{display:'flex', flexWrap:'wrap', gap:'0.5rem', alignItems:'center'}}>
                      {event.participants.filter(p => p).map((p, idx) => (
                        <div key={p._id} className="participant-avatar" style={{
                          width:'44px', height:'44px', borderRadius:'50%', backgroundColor:'rgba(56, 189, 248, 0.2)',
                          display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
                          flexShrink:0, position:'relative', zIndex: event.participants.length - idx
                        }} 
                        onClick={() => navigate(`/user/${p._id}`)}
                        title={`${p.displayName || `${p.firstName} ${p.lastName}`}`}>
                          {p.photoURL ? (
                            <img src={p.photoURL} alt={p.displayName} style={{width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover'}} />
                          ) : (
                            <span style={{fontWeight:700, fontSize:'0.9rem', color:'var(--accent)'}}>
                              {(p.displayName || `${p.firstName} ${p.lastName}`).split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : !isOrganizer && (
                  <div style={{marginBottom:'1rem', paddingBottom:'1rem', borderTop:'1px solid rgba(56, 189, 248, 0.05)', borderBottom:'1px solid rgba(56, 189, 248, 0.05)', paddingTop:'1rem'}}>
                    <p style={{margin:0, fontSize:'0.95rem', fontWeight:600, color:'var(--text)'}}>
                      👥 {event.participants.length}{event.maxParticipants > 0 ? ` / ${event.maxParticipants}` : ''} participants
                    </p>
                  </div>
                )}

                {/* Action Buttons - Enhanced */}
                <div style={{display:'flex', gap:'0.65rem', flexWrap:'wrap', justifyContent:'flex-start', animation:'slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.9s backwards', marginTop:'auto'}}>
                  {currentUser && !isOrganizer && !currentUser.isAdmin && (
                    <>
                      {isJoined ? (
                        <button className="event-btn event-btn-secondary" onClick={() => leaveEvent(event._id)}>
                          ✓ Inscrit
                        </button>
                      ) : (
                        <button className="event-btn event-btn-primary" onClick={() => joinEvent(event._id)} 
                          disabled={event.maxParticipants > 0 && event.participants.length >= event.maxParticipants}
                          style={{opacity: event.maxParticipants > 0 && event.participants.length >= event.maxParticipants ? 0.6 : 1}}>
                          S'inscrire
                        </button>
                      )}
                      <button className="event-btn event-btn-secondary" onClick={() => reportEvent(event._id)}>
                        Signaler
                      </button>
                    </>
                  )}
                  {(isOrganizer || (currentUser && currentUser.isAdmin)) && (
                    <>
                      <button className="event-btn event-btn-secondary" onClick={() => handleEdit(event)}>
                        <MdEdit size={18} />
                        Modifier
                      </button>
                      <button className="event-btn event-btn-danger" onClick={() => removeEvent(event._id)}>
                        <MdDelete size={18} />
                        Supprimer
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
          )}
        </div>
      )}
    </div>
  );
}